import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { http } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const COPY = {
  donation: "Your gift is in. Thank you for keeping poetry free. If you chose to be listed, your name will appear on the Donor Wall after a quick review.",
  ticket: "Your Def Street tickets are confirmed. Stripe has emailed your receipt — bring it (or your name) to the gate.",
  sponsorship: "Thank you for sponsoring MAS Arts. Our team will reach out about your recognition.",
};

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useSite() || {};
  const [state, setState] = useState(result === "success" && sessionId ? "checking" : "cancel");
  const [kind, setKind] = useState("donation");

  useEffect(() => {
    if (state !== "checking") return;
    let n = 0, t;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        setKind(data.kind);
        if (data.payment_status === "paid") { setState("paid"); refresh && refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch (e) {
        if (e?.response?.status === 404) return setState("failed");
      }
      if (++n >= 15) return setState("pending");
      t = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(t);
  }, [state, sessionId, refresh]);

  const view = {
    checking: [Loader2, "Confirming your payment...", "This only takes a moment.", "animate-spin text-mas-terra"],
    paid: [CheckCircle2, "Thank you!", COPY[kind], "text-mas-olive"],
    pending: [Loader2, "Still processing", "Your payment is still being confirmed. You'll get an email receipt from Stripe shortly.", "text-mas-gold"],
    failed: [XCircle, "Payment didn't go through", "No charge was made. Please try again.", "text-mas-terra"],
    cancel: [XCircle, "Checkout cancelled", "No charge was made. You can come back anytime.", "text-mas-mute"],
  }[state];
  const [Icon, title, text, cls] = view;
  return (
    <section className="container-mas py-24 max-w-xl text-center" data-testid={`payment-result-${state}`}>
      <div className="bg-shell text-ink p-8 sm:p-12">
      <Icon className={`w-14 h-14 mx-auto ${cls}`} />
      <h1 className="text-4xl sm:text-5xl font-semibold mt-6">{title}</h1>
      <p className="mt-4 text-mas-mute leading-relaxed">{text}</p>
      <div className="mt-8 flex justify-center gap-3 flex-wrap">
        <Link to="/donate" className="btn-terra" data-testid="payment-result-donor-wall-link">See the Donor Wall</Link>
        <Link to="/" className="btn-ink" data-testid="payment-result-home-link">Back home</Link>
      </div>
      </div>
    </section>
  );
}
